import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Edit3, Eye, Undo2, Redo2, Download, Printer, ChevronRight, BookOpen,
  Loader2, Link2, Send, History,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { downloadDocx, downloadPdf, downloadTxt, generateFilename } from '@/lib/documentExport';
import { ClauseTaggedMarkdown } from '@/components/annotations/ClauseTaggedMarkdown';
import RichDraftEditor from '@/components/draft/RichDraftEditor';
import VersionTimelineDrawer from '@/components/draft/VersionTimelineDrawer';

/**
 * ContractEditor — view + edit contract / analysis-report content.
 *
 * Features:
 *   • View mode (rendered Markdown) / Edit mode (textarea, monospace)
 *   • Local undo/redo stack while editing
 *   • Section navigation sidebar built from headings
 *   • Citations footer with linked sources
 *   • Hover highlight on clauses (h2 / h3 markdown headers)
 *   • Download buttons (.txt now, .docx / .pdf delegate via onDownload)
 *   • Print-friendly via @media print rules in harvey-design.css
 *
 * Props
 *   content     — string (Markdown)
 *   onChange    — (next: string) => void
 *   onSave      — (current: string) => void
 *   onDownload  — (format: 'docx' | 'pdf' | 'txt', content: string) => void  (optional)
 *   citations   — Array<{id, source, text, url?}>
 *   mode        — 'view' | 'edit' (default 'view')
 *   readOnly    — boolean (forces view mode)
 *   title       — optional document title
 *   harveyTheme — boolean (default true) — Harvey design tokens
 */
export const ContractEditor = ({
  content = '',
  onChange,
  onSave,
  onDownload,
  onAction,            // (id: 'review' | 'redraft' | 'compliance' | 'improve') => void
  showActions = false, // when true, render the bottom action bar
  citations = [],
  mode: initialMode = 'view',
  readOnly = false,
  title,
  contractType = null,  // 'nda' | 'employment_contract' | …  — drives filename
  // Cluster B-5 (Feb 06 2026): server-emitted clause map for Phase-2
  // annotations. When non-empty, view-mode renders via
  // ClauseTaggedMarkdown so each clause is wrapped in
  // <span data-clause-id="clause_N">…</span>. The popover anchors
  // to selections inside these spans.
  clauseBoundaries = null,
  // Cluster F.4 (Feb 2026): when set, the toolbar renders a
  // "Copy link" button that emits the CANONICAL `/chat/:matter_id`
  // URL (regardless of the current URL bar shape — so legacy
  // `/chat#session=<id>` callers still get the canonical link).
  matterId = null,
  harveyTheme = true,
}) => {
  const { language } = useLanguage();
  const isArabic = language === 'ar';

  // Cluster H.2 (Feb 2026): rich-editor opt-in via localStorage flag.
  // Flag-OFF callers keep the classic textarea — zero behaviour change.
  // Flag-ON callers replace edit-mode with RichDraftEditor (Tiptap).
  // Read on every render — cheap and lets users toggle live without
  // a full reload during dev.
  const richEditorEnabled = typeof window !== 'undefined' &&
    window.localStorage?.getItem('bayenah_rich_editor') === '1';

  const [mode, setMode] = useState(readOnly ? 'view' : initialMode);
  const [text, setText] = useState(content);
  const [history, setHistory] = useState([content]);
  const [historyIdx, setHistoryIdx] = useState(0);
  const containerRef = useRef(null);

  // External content updates flush local state + reset history — but
  // ONLY in view mode. Cluster B-4 (Feb 06 2026): in edit mode the
  // textarea is a controlled input; if we kept syncing `content` →
  // `text` while the user is typing, every server-side echo (or
  // mid-stream re-render from the parent) would clobber the typed
  // characters and slam the caret to position 0. Edit-mode text now
  // lives purely in local state until the user saves; entering edit
  // mode (mode === 'edit') from view mode starts with the current
  // content via the mode-transition effect below.
  useEffect(() => {
    if (mode === 'edit') return; // don't touch the user's draft while editing
    setText(content);
    setHistory([content]);
    setHistoryIdx(0);
  }, [content, mode]);

  // Snapshot `content` into local state when the user enters edit
  // mode so they start from the currently-rendered view content.
  const prevModeRef = useRef(mode);
  useEffect(() => {
    if (prevModeRef.current !== 'edit' && mode === 'edit') {
      setText(content);
      setHistory([content]);
      setHistoryIdx(0);
    }
    prevModeRef.current = mode;
  }, [mode, content]);

  const sections = useMemo(() => extractSections(text), [text]);

  const pushHistory = (next) => {
    setHistory((prev) => {
      const trimmed = prev.slice(0, historyIdx + 1);
      const newHistory = [...trimmed, next];
      return newHistory.slice(-100); // cap at 100 to avoid memory bloat
    });
    setHistoryIdx((idx) => Math.min(idx + 1, 99));
  };

  const handleEdit = (e) => {
    const next = e.target.value;
    setText(next);
    pushHistory(next);
    onChange?.(next);
  };

  const undo = () => {
    if (historyIdx <= 0) return;
    const next = history[historyIdx - 1];
    setHistoryIdx(historyIdx - 1);
    setText(next);
    onChange?.(next);
  };

  const redo = () => {
    if (historyIdx >= history.length - 1) return;
    const next = history[historyIdx + 1];
    setHistoryIdx(historyIdx + 1);
    setText(next);
    onChange?.(next);
  };

  const scrollToSection = (id) => {
    const el = containerRef.current?.querySelector(`[data-section-id="${id}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const [downloading, setDownloading] = useState(null); // 'docx' | 'pdf' | null
  const [versionDrawerOpen, setVersionDrawerOpen] = useState(false);

  const handleDownload = async (format) => {
    // Generate a content-aware filename: extracts party names from the
    // Markdown preamble when present, falls back to "<DocType>_<date>".
    const fileName = generateFilename(text, contractType);
    try {
      if (format === 'txt') {
        downloadTxt(text, fileName);
        return;
      }
      setDownloading(format);
      if (format === 'docx') {
        await downloadDocx(text, fileName, { isArabic });
      } else if (format === 'pdf') {
        await downloadPdf(text, fileName, { isArabic, title });
      }
    } catch (err) {
      console.error('[ContractEditor] download failed', err);
      toast.error(isArabic ? 'تعذّر تنزيل الملف. حاول مرة أخرى.' : 'Download failed — please try again.');
    } finally {
      setDownloading(null);
    }
    onDownload?.(format, text);
  };

  return (
    <div
      data-theme={harveyTheme ? 'harvey' : undefined}
      dir={isArabic ? 'rtl' : 'ltr'}
      className={cn(
        'flex flex-col h-full hv-surface',
        isArabic ? 'font-arabic' : 'font-plex-sans',
      )}
      data-testid="contract-editor"
    >
      {/* Toolbar */}
      {/* Jul 2026 mobile fix: the toolbar holds ~10 actions in one row —
          at phone widths it overflowed the viewport. overflow-x-auto +
          nowrap makes it swipeable on mobile; desktop is unaffected. */}
      <div className="hv-print-hide flex items-center gap-1.5 px-3 py-2 hv-border-b text-sm overflow-x-auto whitespace-nowrap">
        {title && <span className="font-medium hv-text mr-3 truncate max-w-[40%]">{title}</span>}
        <div className="flex-1" />
        {!readOnly && (
          <>
            <ToolbarButton onClick={() => setMode(mode === 'view' ? 'edit' : 'view')} icon={mode === 'view' ? Edit3 : Eye} testId="contract-editor-mode-toggle">
              {mode === 'view' ? (isArabic ? 'تعديل' : 'Edit') : (isArabic ? 'عرض' : 'View')}
            </ToolbarButton>
            <ToolbarButton onClick={undo} icon={Undo2} disabled={historyIdx === 0} testId="contract-editor-undo">
              {isArabic ? 'تراجع' : 'Undo'}
            </ToolbarButton>
            <ToolbarButton onClick={redo} icon={Redo2} disabled={historyIdx >= history.length - 1} testId="contract-editor-redo">
              {isArabic ? 'إعادة' : 'Redo'}
            </ToolbarButton>
            {onSave && (
              <ToolbarButton onClick={() => onSave(text)} icon={null} testId="contract-editor-save">
                {isArabic ? 'حفظ' : 'Save'}
              </ToolbarButton>
            )}
          </>
        )}
        <span className="mx-1 hv-text-subtle">|</span>
        {matterId && (
          <ToolbarButton
            onClick={async () => {
              const url = `${window.location.origin}/chat/${encodeURIComponent(matterId)}`;
              try {
                await navigator.clipboard.writeText(url);
                toast.success(isArabic ? 'تم نسخ الرابط' : 'Link copied');
              } catch (_e) {
                toast.error(isArabic ? 'تعذّر نسخ الرابط' : 'Copy failed');
              }
            }}
            icon={Link2}
            testId="contract-editor-copy-link"
          >
            {isArabic ? 'نسخ الرابط' : 'Copy link'}
          </ToolbarButton>
        )}
        {matterId && (
          <ToolbarButton
            onClick={() => {
              // Cluster H.4: open Herald panel via custom event.
              // `useHeraldContext` (URL-driven) automatically enriches
              // context with {page:"matter_draft", label:matterId,
              // attachments:[{type:"matter", id:matterId}]} since we
              // are on /chat/:matter_id.
              window.dispatchEvent(new CustomEvent('bayenah:open-herald-panel'));
            }}
            icon={Send}
            testId="contract-editor-send-to-colleague"
          >
            {isArabic ? 'إرسال إلى زميل' : 'Send to colleague'}
          </ToolbarButton>
        )}
        {matterId && (
          <ToolbarButton
            onClick={() => setVersionDrawerOpen(true)}
            icon={History}
            testId="contract-editor-versions"
          >
            {isArabic ? 'الإصدارات' : 'Versions'}
          </ToolbarButton>
        )}
        <ToolbarButton onClick={() => handleDownload('txt')} icon={Download} testId="contract-editor-download-txt">
          .txt
        </ToolbarButton>
        <ToolbarButton onClick={() => handleDownload('docx')} icon={downloading === 'docx' ? Loader2 : Download} disabled={downloading !== null} testId="contract-editor-download-docx">
          .docx
        </ToolbarButton>
        <ToolbarButton onClick={() => handleDownload('pdf')} icon={downloading === 'pdf' ? Loader2 : Download} disabled={downloading !== null} testId="contract-editor-download-pdf">
          .pdf
        </ToolbarButton>
        <ToolbarButton onClick={() => window.print()} icon={Printer} testId="contract-editor-print">
          {isArabic ? 'طباعة' : 'Print'}
        </ToolbarButton>
      </div>

      {/* Body: section nav + main */}
      <div className="flex flex-1 min-h-0">
        {sections.length > 0 && (
          <nav
            className="hv-print-hide hidden md:block w-56 shrink-0 hv-border-l overflow-y-auto py-3 px-2"
            data-testid="contract-editor-sections"
          >
            <div className="flex items-center gap-1.5 px-2 py-1 hv-text-muted text-xs uppercase tracking-wide">
              <BookOpen className="w-3 h-3" />
              {isArabic ? 'الأقسام' : 'Sections'}
            </div>
            <ol className="mt-1 space-y-0.5">
              {sections.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => scrollToSection(s.id)}
                    className={cn(
                      'w-full flex items-center gap-1.5 px-2 py-1.5 text-sm hv-text-muted hover:hv-text rounded transition-colors',
                      isArabic && 'flex-row-reverse text-right',
                      s.level === 3 && 'pl-5',
                    )}
                    data-testid={`contract-editor-section-${s.id}`}
                  >
                    <ChevronRight className={cn('w-3 h-3 shrink-0', isArabic && 'scale-x-[-1]')} />
                    <span className="truncate">{s.title}</span>
                  </button>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <div ref={containerRef} className="flex-1 min-w-0 overflow-y-auto px-6 md:px-10 py-6">
          {mode === 'edit' ? (
            richEditorEnabled ? (
              <RichDraftEditor
                content={text}
                onChange={(html) => onChange?.(html)}
                isArabic={isArabic}
                matterId={matterId}
                onCommentClause={({ clauseId }) => {
                  // Cluster H.2 (Feb 2026): paragraph "Comment" gutter
                  // action surfaces the existing AnnotationPopover via
                  // the same window-event pattern H.4 uses for Herald.
                  window.dispatchEvent(new CustomEvent('bayenah:open-annotation-popover', {
                    detail: { clauseId, matterId },
                  }));
                }}
              />
            ) : (
              <textarea
                value={text}
                onChange={handleEdit}
                spellCheck={false}
                data-testid="contract-editor-textarea"
                className={cn(
                  'w-full h-full min-h-[60vh] resize-none bg-transparent border-0 outline-none',
                  'text-[14px] leading-7 hv-text whitespace-pre-wrap',
                  isArabic ? 'font-arabic' : 'font-plex-sans',
                )}
                dir={isArabic ? 'rtl' : 'ltr'}
              />
            )
          ) : (
            <article
              className={cn(
                'max-w-none leading-relaxed',
                isArabic && 'text-right',
              )}
              style={{ color: 'var(--text-primary)', fontSize: '14px', lineHeight: 1.8 }}
              data-testid="contract-editor-article"
            >
              {Array.isArray(clauseBoundaries) && clauseBoundaries.length > 0 ? (
                <ClauseTaggedMarkdown
                  content={text}
                  boundaries={clauseBoundaries}
                  components={makeMarkdownComponents()}
                />
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={makeMarkdownComponents()}
                >
                  {text}
                </ReactMarkdown>
              )}
            </article>
          )}

          {/* Citations footer */}
          {citations?.length > 0 && (
            <footer
              className="mt-10 pt-4 hv-border-t"
              data-testid="contract-editor-citations"
            >
              <h3 className="text-xs uppercase tracking-wide hv-text-muted mb-2">
                {isArabic ? 'المراجع النظامية' : 'Cited Sources'}
              </h3>
              <ol className="space-y-1.5 text-sm">
                {citations.map((c, idx) => (
                  <li key={c.id ?? idx} className={cn('hv-text-muted', isArabic && 'text-right')}>
                    <span className="hv-tag-policy mr-1.5">[{idx + 1}]</span>
                    {c.url ? (
                      <a href={c.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        {c.source}
                      </a>
                    ) : (
                      <span>{c.source}</span>
                    )}
                    {c.text && <span className="hv-text-subtle"> — {c.text}</span>}
                  </li>
                ))}
              </ol>
            </footer>
          )}
        </div>
      </div>

      {/* Bottom action bar — shown only for completed drafts */}
      {showActions && onAction && (
        <div
          dir={isArabic ? 'rtl' : 'ltr'}
          className={cn('hv-print-hide flex items-center gap-2 px-4 py-3 hv-border-t flex-wrap', isArabic && 'flex-row-reverse')}
          style={{ background: 'var(--surface)' }}
          data-testid="contract-editor-actions"
        >
          <ActionButton id="review"     onClick={onAction} testId="ce-action-review">
            {isArabic ? 'مراجعة المسودة' : 'Review draft'}
          </ActionButton>
          <ActionButton id="redraft"    onClick={onAction} testId="ce-action-redraft">
            {isArabic ? 'إعادة الصياغة' : 'Redraft'}
          </ActionButton>
          <ActionButton id="compliance" onClick={onAction} testId="ce-action-compliance">
            {isArabic ? 'فحص الامتثال' : 'Compliance check'}
          </ActionButton>
          <ActionButton id="improve"    onClick={onAction} testId="ce-action-improve">
            {isArabic ? 'تحسين المسودة' : 'Improve draft'}
          </ActionButton>
        </div>
      )}

      {/* Cluster H.3 (Feb 2026) — version timeline drawer */}
      {matterId && (
        <VersionTimelineDrawer
          open={versionDrawerOpen}
          onClose={() => setVersionDrawerOpen(false)}
          sessionId={matterId}
          currentContent={text}
          isArabic={isArabic}
          onRestored={(newContent) => {
            // Update local editor state + propagate to parent so the
            // surrounding chat surface persists the restore.
            setText(newContent);
            setHistory([newContent]);
            setHistoryIdx(0);
            onChange?.(newContent);
            toast.success(isArabic ? 'تمت استعادة الإصدار' : 'Version restored');
          }}
        />
      )}
    </div>
  );
};

function ActionButton({ id, onClick, children, testId }) {
  const handleClick = (e) => {
    onClick(id);
    // Drop focus immediately so the button doesn't keep its :focus / pressed
    // visual state after the click — Harvey buttons should feel transient.
    setTimeout(() => e.currentTarget?.blur?.(), 0);
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'inline-flex items-center px-3 py-1.5 text-[12.5px] transition focus:outline-none',
        'bg-white border border-[#E0E0E0] text-[#111111]',
        'hover:border-[#AAAAAA] active:bg-[#F0F0EE]',
      )}
      style={{ borderRadius: '9999px' }}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────
function ToolbarButton({ children, icon: Icon, onClick, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 text-sm rounded',
        'hv-text-muted hover:hv-text hover:bg-black/5 transition-colors',
        disabled && 'opacity-40 cursor-not-allowed',
      )}
    >
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {children}
    </button>
  );
}

function extractSections(markdown) {
  if (!markdown) return [];
  const lines = markdown.split('\n');
  const out = [];
  for (const line of lines) {
    const m = /^(#{2,3})\s+(.+?)\s*$/.exec(line);
    if (!m) continue;
    const level = m[1].length;
    const title = m[2].trim();
    if (!title) continue;
    out.push({ id: slugify(title), title, level });
  }
  return out;
}

function slugify(s) {
  return s.toLowerCase().replace(/[^\w\u0600-\u06FF]+/g, '-').replace(/^-+|-+$/g, '');
}

function makeMarkdownComponents() {
  const baseColor = '#111111';
  return {
    h1: ({ children }) => (
      <h1 data-section-id={slugify(plain(children))}
          style={{ fontSize: '24px', fontWeight: 600, marginTop: '24px', marginBottom: '12px', color: baseColor }}>
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 data-section-id={slugify(plain(children))}
          style={{ fontSize: '19px', fontWeight: 600, marginTop: '24px', marginBottom: '10px', color: baseColor }}
          className="hover:bg-black/[0.03] -mx-2 px-2 rounded transition-colors">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 data-section-id={slugify(plain(children))}
          style={{ fontSize: '16px', fontWeight: 600, marginTop: '20px', marginBottom: '8px', color: baseColor }}
          className="hover:bg-black/[0.03] -mx-2 px-2 rounded transition-colors">
        {children}
      </h3>
    ),
    p:  ({ children }) => <p style={{ marginBottom: '12px', lineHeight: 1.8 }}>{children}</p>,
    strong: ({ children }) => <strong style={{ fontWeight: 600, color: baseColor }}>{children}</strong>,
    em:     ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
    hr:     () => <hr style={{ border: 0, borderTop: '1px solid #E0E0E0', margin: '20px 0' }} />,
    ul:     ({ children }) => <ul style={{ paddingInlineStart: '24px', marginBottom: '12px', listStyle: 'disc' }}>{children}</ul>,
    ol:     ({ children }) => <ol style={{ paddingInlineStart: '24px', marginBottom: '12px', listStyle: 'decimal' }}>{children}</ol>,
    li:     ({ children }) => <li style={{ marginBottom: '4px' }}>{children}</li>,
    blockquote: ({ children }) => (
      <blockquote style={{ borderInlineStart: '3px solid #D0D0D0', paddingInlineStart: '12px', color: '#555', marginBottom: '12px' }}>
        {children}
      </blockquote>
    ),
    code: ({ children }) => <code style={{ background: '#F5F3EF', padding: '2px 6px', borderRadius: '4px', fontSize: '13px' }}>{children}</code>,
    table: ({ children }) => <table className="hv-table my-3">{children}</table>,
  };
}

function plain(node) {
  if (typeof node === 'string') return node;
  if (Array.isArray(node)) return node.map(plain).join('');
  if (node && node.props && node.props.children) return plain(node.props.children);
  return '';
}

export default ContractEditor;
